import { atom, useAtomValue, useSetAtom } from 'jotai';
import { useCallback, useEffect, useRef } from 'react';

/**
 * Routine ids with a run currently in flight — kicked off and/or waiting on
 * the agent's response. Drives the "Running" byline on the routine cards.
 * Mirrored from the run surfaces (kick-off pending + the run chat's
 * turn-in-flight), so it clears back to the default byline as soon as the
 * response lands or the run surface goes away.
 */
export const runningWorkflowIdsAtom = atom<Record<string, boolean>>({});

/** Is this routine currently running (for the card's Running byline)? */
export function useIsRoutineRunning(workflowId: string): boolean {
  return Boolean(useAtomValue(runningWorkflowIdsAtom)[workflowId]);
}

/**
 * Set/clear one routine's running flag, returning the previous map untouched
 * when nothing would change (so subscribers don't re-render on a no-op).
 */
export const withRunningFlag = (
  prev: Record<string, boolean>,
  id: string,
  running: boolean
): Record<string, boolean> => {
  if (running) return prev[id] ? prev : { ...prev, [id]: true };
  if (!prev[id]) return prev;
  const rest = { ...prev };
  delete rest[id];
  return rest;
};

/**
 * Mark/unmark this routine as running. Clears its flag on unmount so a
 * surface that disappears mid-flight can't leave a stale "Running" badge.
 */
export function useMarkRoutineRunning(
  workflowId: string | null | undefined
): (running: boolean) => void {
  const setRunning = useSetAtom(runningWorkflowIdsAtom);
  const idRef = useRef(workflowId);
  idRef.current = workflowId;

  const mark = useCallback(
    (running: boolean) => {
      const id = idRef.current;
      if (!id) return;
      setRunning((prev) => withRunningFlag(prev, id, running));
    },
    [setRunning]
  );

  useEffect(() => () => mark(false), [mark]);

  return mark;
}
